import React from 'react'
import Icon from 'UI/FontAwesomeIcon'
import moment from 'moment'
import { isEmpty, isNil, isPresent } from 'lodash'
import CSRFToken from 'UI/CSRFToken'

const useColumns = ({
  setImportIDToRemove: onRemove,
  canDestroy,
  tab,
  setImportConfigurationToCreate: onCreate
}) => {
  const isNotToday = (date) => (
    new Date().toDateString() !== new Date(date).toDateString()
  )

  const compliantColumn = (
    {
      Header: 'Compliant?',
      accessor: 'isCompliant',
      width: 80,
      Cell: (row) => (
        <div className="text-center">
          <Icon
            className={`
              ${row.value == true && 'text-green-700 text-2xl'}
              ${row.value == false && 'text-red-700 transform rotate-45 text-2xl'}
              ${row.value == null && 'text-gray-700 text-2xl font-light'}
            `}
            icon={`
              ${row.value == true && 'fa-check'}
              ${row.value == false && 'fa-plus'}
              ${row.value == null && 'fa-minus'}
            `}
          />
        </div>
      )
    }
  )

  const callbackCompliantColumn = (
    {
      Header: "Today's Audits",
      accessor: 'callbackIssues',
      width: 80,
      Cell: ({ value }) => (
        <div className="text-center">
          <Icon className={`${isEmpty(value) ? 'text-green-700 text-2xl fa-check' : 'text-red-700 transform rotate-45 text-2xl fa-plus' }`} />
        </div>
      )
    }
  )

  const dataGatheredAtColumn = (
    {
      Header: 'Data Gathered At',
      accessor: 'dataGatheredAt',
      width: 180,
      Cell: (row) => (
        <>
          {isNil(row.value) && <Icon icon="minus" className="text-2xl text-gray-700" />}

          {isPresent(row.value) && (
            <div className={`${isNotToday(row.value) && 'text-red-700 font-medium'}`}>
              {moment.utc(new Date(row.value)).format('YYYY-MM-DD  hh:mm A')}
            </div>
          )}
        </>
      )
    }
  )

  const complianceCheckedAtColumn = (
    {
      Header: 'Compliance Checked At',
      accessor: 'complianceCheckedAt',
      width: 180,
      Cell: (row) => (
        <>
          {isNil(row.value) && <Icon icon="minus" className="text-2xl text-gray-700" />}

          {isPresent(row.value) && (
            <div className={`${isNotToday(row.value) && 'text-red-700 font-medium'}`}>
              {moment.utc(new Date(row.value)).format('YYYY-MM-DD  hh:mm A')}
            </div>
          )}
        </>
      )
    }
  )

  const actionColumn = (
    {
      Header: 'Actions',
      width: 100,
      Cell: ({ original: { id } }) => (
        <div className="text-center">
          {canDestroy ? (
            <div className="flex">
              <form action={`/system/import_configurations/import/${id}`} method='POST'>
                <CSRFToken />
                <button
                  onClick={() => document.querySelector(`.import__sync--${id}`).classList.add('animate-spin')}
                  type="submit"
                >
                  <Icon
                    id
                    title="Refresh"
                    className={`import__sync--${id} text-lg text-gray-600 p-2 rounded-full cursor-pointer duration-300 hover:bg-blue-50 hover:text-blue-600`}
                    icon="sync"
                  />
                </button>

              </form>

              <Icon
                title="Remove Assets"
                className={`text-lg text-gray-600 p-2 rounded-full cursor-pointer duration-300 hover:bg-red-50 hover:text-red-600`}
                icon="trash"
                onClick={() => onRemove(id)}
              />
            </div>) : null}
        </div>
      )
    }
  )

  const lastAuditColumn = (
    {
      Header: 'Last Audit',
      accessor: 'lastAudit',
      width: 80,
      Cell: (row) => (
        <div className="flex justify-center items-center">
          <Icon
            className={`
              ${row.value?.successful == true && 'text-green-700 text-2xl'}
              ${row.value?.successful == false && 'text-red-700 transform rotate-45 text-2xl'}
              ${row.value?.successful == null && 'text-gray-700 text-2xl font-light'}
            `}
            icon={`
              ${row.value?.successful == true && 'fa-check'}
              ${row.value?.successful == false && 'fa-plus'}
              ${row.value?.successful == null && 'fa-minus'}
            `}
          />
  
          {
            row.value?.error && (
              <Icon
                className="ml-2 text-xl cursor-pointer duration-300 hover:text-red-600"
                icon="info-circle"
                title={row.value?.error}
              />
            )
          }
        </div>
      )
    }
  )

  const createColumn = (
    {
      Header: 'Actions',
      width: 70,
      Cell: ({ original }) => (
        <div
          className="text-center cursor-pointer mx-auto border-2 border-gray-400 text-gray-400 rounded-full h-6 w-6 duration-200 hover:border-blue-600 hover:text-blue-600 flex items-center justify-center"
          onClick={() => onCreate(original)}
        >
          <Icon
            title="Create Import Configuration"
            className="text-base text-center"
            icon="plus"
          />
        </div>
      )
    }
  )

  const importColumns = [
    { Header: 'ID', accessor: 'id', width: 50 },
    { Header: 'Resource', accessor: 'resource' },
    { Header: 'Location', accessor: 'location', width: 200 },
    { Header: 'Customer', accessor: 'customer', width: 200 },
    compliantColumn,
    lastAuditColumn,
    { Header: 'Duration', accessor: 'lastAudit.importDuration', width: 80 },
    { Header: 'File Size', accessor: 'lastAudit.fileSize', width: 80 },
    { Header: 'File Rows', accessor: 'lastAudit.fileRowCount', width: 80 },
    dataGatheredAtColumn,
    complianceCheckedAtColumn,
    actionColumn
  ]
  
  const callbackColumns = [
    { Header: 'ID', accessor: 'id', width: 50 },
    { Header: 'Resource', accessor: 'resource', width: 400 },
    { Header: 'Customer', accessor: 'customer', width: 200 },
    callbackCompliantColumn,
    { Header: 'Duration', accessor: 'lastAudit.importDuration', width: 80 },
    dataGatheredAtColumn
  ]

  const newFileColumns = [
    { Header: 'ID', accessor: 'id', width: 50 },
    { Header: 'Resource', accessor: 'resource' },
    lastAuditColumn,
    { Header: 'Duration', accessor: 'lastAudit.importDuration', width: 150 },
    { Header: 'File Size', accessor: 'lastAudit.fileSize', width: 150 },
    { Header: 'File Rows', accessor: 'lastAudit.fileRowCount', width: 150 },
    dataGatheredAtColumn,
    createColumn
  ]
  
  if (tab === 'callbacks') return callbackColumns
  else if (tab === 'imports') return importColumns
  else if (tab === 'new_files') return newFileColumns
}

export default useColumns
